import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getApiBaseUrl } from '../../utils/apiBase';
import './landing-eb-demo.css';

/**
 * "Book a demo" modal, available site-wide.
 * Wrap the app (inside <Router>) in <DemoModalProvider>, then in any component:
 *   const { openDemo } = useDemoModal();
 *   <button onClick={openDemo}>Book a demo</button>
 */
const DemoCtx = createContext({ openDemo: () => window.location.assign('/enquiry') });
export const useDemoModal = () => useContext(DemoCtx);

const SIZES = ['Under 50', '50 to 250', '250 to 1,000', '1,000+'];
const INDUSTRIES = [
  'Oil, Gas and Energy', 'Healthcare and Pharma', 'BFSI', 'Manufacturing',
  'Aviation and Aerospace', 'Retail', 'Logistics', 'IT and Software', 'Other',
];
const EMPTY = { full_name: '', email: '', company: '', phone: '', size: SIZES[0], industry: INDUSTRIES[0], wanted: '' };

const DemoModal = ({ onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [firstName, setFirstName] = useState('');
  const firstRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (firstRef.current) firstRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const err = {};
    if (!form.full_name) err.full_name = 'Required';
    else if (form.full_name.length < 3) err.full_name = 'At least 3 characters';
    if (!form.email) err.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = 'Invalid email';
    if (!form.phone) err.phone = 'Required';
    else if (!/^[0-9+\-\s()]{8,20}$/.test(form.phone)) err.phone = 'Invalid phone';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const message = [
        'Topic: Book a demo',
        form.company ? `Company: ${form.company}` : null,
        `Team size: ${form.size}`,
        `Industry: ${form.industry}`,
        form.wanted ? `\nWould like to see: ${form.wanted}` : null,
      ].filter(Boolean).join('\n');

      const res = await axios.post(`${getApiBaseUrl()}/demo-requests`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        size: form.size,
        industry: form.industry,
        wanted: form.wanted,
        sourcePage: window.location.pathname,
      });
      
      if (res.data?.success) {
        setFirstName(form.full_name.trim().split(' ')[0] || 'there');
        setSent(true);
        setForm(EMPTY);
      } else {
        toast.error(res.data?.message || 'Failed to send your request.');
      }
    } catch (error) {
      console.error('Demo request error:', error);
      toast.error(error.response?.data?.message || 'Failed to send your request. Please try again or email engage@infylearn.net.');
    } finally {
      setLoading(false);
    }
  };

  const input = (name, label, props = {}, ref) => (
    <label className="eb-demo-field">
      {label}
      <input
        ref={ref}
        name={name}
        value={form[name]}
        onChange={handleChange}
        className={errors[name] ? 'is-bad' : ''}
        {...props}
      />
      {errors[name] && <em>{errors[name]}</em>}
    </label>
  );

  return createPortal(
    <div
      className="eb-demo-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="eb-demo-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="eb-demo-card">
        <div className="eb-demo-head">
          <div>
            <span className="eb-demo-eyebrow">BOOK A DEMO · 30 MINUTES</span>
            <h2 id="eb-demo-title">See EthosBoard working on your roles</h2>
          </div>
          <button type="button" className="eb-demo-x" aria-label="Close" onClick={onClose}>×</button>
        </div>

        {sent ? (
          <div className="eb-demo-sent">
            <h3>Thanks, {firstName}</h3>
            <p>We have your request and will reply within 1 business day to schedule your walkthrough.</p>
            <button type="button" className="eb-demo-send" onClick={onClose}>Close</button>
          </div>
        ) : (
          <form className="eb-demo-body" onSubmit={submit} noValidate>
            <div className="eb-demo-grid">
              {input('full_name', 'Full name', { placeholder: 'Amal Rahman', autoComplete: 'name' }, firstRef)}
              {input('email', 'Work email', { type: 'email', placeholder: 'amal@company.com', autoComplete: 'email' })}
              {input('company', 'Company', { placeholder: 'Company name', autoComplete: 'organization' })}
              {input('phone', 'Phone', { type: 'tel', placeholder: '+971 50 000 0000', autoComplete: 'tel' })}
              <label className="eb-demo-field">
                Team size
                <select name="size" value={form.size} onChange={handleChange}>
                  {SIZES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
              <label className="eb-demo-field">
                Industry
                <select name="industry" value={form.industry} onChange={handleChange}>
                  {INDUSTRIES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <label className="eb-demo-field">
              What would you like to see?
              <textarea
                name="wanted"
                rows={3}
                placeholder="e.g. Compliance readiness for 400 field staff"
                value={form.wanted}
                onChange={handleChange}
              />
            </label>
            <div className="eb-demo-foot">
              <span>By submitting you agree to be contacted about your request. <Link to="/privacy-policy" onClick={onClose}>Privacy notice</Link>.</span>
              <button type="submit" className="eb-demo-send" disabled={loading}>{loading ? 'Sending…' : 'Request demo'}</button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
};

export const DemoModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openDemo = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  return (
    <DemoCtx.Provider value={{ openDemo }}>
      {children}
      {open && <DemoModal onClose={close} />}
    </DemoCtx.Provider>
  );
};

export default DemoModal;
